import { produce } from 'immer';
import { useRouter } from 'next/navigation';
import { createParser, useQueryState } from 'nuqs';
import React, { useTransition } from 'react';

import { makeKiaFirst } from '@/services/filters/filters.service';
import { BrandAndRangesType } from '@/services/filters/new-filters.service';

import { NestedCheckboxesSimple } from '../../../nested-checkboxes-simple/nested-checkboxes';
import { NewFilter } from '../new-filter/new-filter';
import { isFilterEqual, useClearSearch, useQueryNonce } from '../utils';

export const NewBrandsRanges: React.FC<{
  brandsAndRanges: BrandAndRangesType;
  count: number;
  defaultMake?: string;
  defaultModel?: string;
}> = ({ brandsAndRanges, count, defaultMake, defaultModel }) => {
  const router = useRouter();

  const { generate } = useQueryNonce();

  const mappedBrandsAndRanges = brandsAndRanges.map((brand) => {
    return {
      id: brand.id,
      name: brand.name,
      isChecked: defaultMake?.toLowerCase() === brand.name.toLowerCase(),
      isOpened: false,
      vehicleCount: brand.vehicles.length,
      children: brand.range_ids?.map((range) => {
        return {
          id: range.id,
          name: range.name,
          isChecked:
            defaultModel?.toLowerCase() === range.name.toLowerCase() ||
            (defaultMake?.toLowerCase() === brand.name.toLowerCase() &&
              !defaultModel),
          isOpened: false,
          vehicleCount: range.vehicles.length,
        };
      }),
    };
  });

  const parseBrandRanges = createParser({
    parse(queryValue) {
      const lowerCaseQuery = queryValue.toLowerCase();

      return mappedBrandsAndRanges.map((brand) => {
        brand.isChecked = lowerCaseQuery.includes(brand.name.toLowerCase());
        brand.children = brand.children.map((range) => {
          const checkAll = brand.isChecked && !lowerCaseQuery.includes('-');

          range.isChecked =
            lowerCaseQuery.includes(range.name.toLowerCase()) || checkAll;
          return range;
        });

        return brand;
      });
    },
    serialize(value) {
      const filterList: string[] = [];

      for (const brand of value) {
        if (brand.children.every((child) => child.isChecked)) {
          filterList.push(brand.name);
        } else if (brand.children.some((child) => child.isChecked)) {
          for (const range of brand.children.filter(
            (child) => child.isChecked,
          )) {
            filterList.push(`${brand.name}-${range.name}`);
          }
        }
      }

      return filterList.join(',');
    },
    eq(a, b) {
      const eq = isFilterEqual(a, b);

      return eq;
    },
  });

  const [_, startTransition] = useTransition();

  const [queryBrandRanges, setQueryBrandRanges] = useQueryState(
    'makemodel',
    parseBrandRanges
      .withDefault(mappedBrandsAndRanges)
      .withOptions({ startTransition, shallow: false }),
  );

  const toggleFilter = async (name: string) => {
    return await setQueryBrandRanges((options) => {
      return produce(options, (draftOptions) => {
        draftOptions?.forEach((option) => {
          if (option.name === name) {
            option.isChecked = !option.isChecked;

            option.children?.forEach((child) => {
              child.isChecked = option.isChecked;
            });
          } else {
            option.children?.forEach((child) => {
              if (child.name === name) {
                child.isChecked = !child.isChecked;
              }
            });
          }
        });
      });
    });
  };

  const clearSearch = useClearSearch();

  return (
    <NewFilter
      label="Make & Models"
      count={count}
      onClear={() => {
        setQueryBrandRanges(null);
        generate();
      }}
    >
      <NestedCheckboxesSimple
        selectFilter={async (option) => {
          const query = await toggleFilter(option.name);

          clearSearch();

          if (defaultMake || defaultModel) {
            const newUrl = `/used-cars/search?${query}`;

            router.push(newUrl);
            return;
          }
        }}
        options={queryBrandRanges ? makeKiaFirst(queryBrandRanges) : []}
        setOptionsState={async (newState) => {
          const query = await setQueryBrandRanges(newState as any);

          clearSearch();

          if (defaultMake || defaultModel) {
            const newUrl = `/used-cars/search?${query}`;

            router.push(newUrl);
            return;
          }
        }}
        resetText="Reset Filters"
        selectAllText="All"
        hideText="Hide Models"
        openText="Show Models"
        allSelected={queryBrandRanges?.every((item) => item.isChecked) || false}
      />
    </NewFilter>
  );
};
